import { BUS_MESSAGE_KEY } from 'src/app/shared/constants/message-bus';
import { Component, OnInit } from '@angular/core';
import { DataService } from 'src/app/features/dfte-grid-view/config/data.service';
import { AdminService } from '../admin-service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MessagingService } from 'src/app/shared/services/messaging.service';
import { MessageService } from 'src/app/features/dfte-grid-view/services/message.service';
import { jwtDecode } from 'jwt-decode';
import { IBasicAuthUserBackendTokenPayload } from 'src/app/shared/constants/dfte-user-info';

// Define the User interface with correct types
interface User {
  username: string;
  name: string;
  addedOn: string; // Adjusted to match 'addedOn' in the code
  added_by: string;
  isAdmin: number;
  isUserActive: boolean;
}

@Component({
  selector: 'app-admin',
  templateUrl: './admin.component.html',
  styleUrls: ['./admin.component.scss'],
})
export class AdminComponent implements OnInit {
  currentPage: number = 1;
  itemsPerPage: number = 10;
  totalPages: number = 0;
  userarray: User[] = [];
  displayedUsers: User[] = [];
  totalUsers: number = 0;
  itemsPerPageOptions = [5, 10, 20, 50];
  addUser: boolean = false;
  editUser: boolean = false;
  resetPassword: boolean = false;
  FormLabel = 'Add User';
  addUserForm!: FormGroup;
  editUserForm!: FormGroup;
  resetPasswordForm!: FormGroup;
  currentLoggedInUser: any;
  userName: string = '';
  private subscription: Subscription;
  loggedInAdminUsername: string = '';
  selectedUserToEdit: any;
  initialUserData: any;

  showStatusFilterPopup: boolean = false;
  statusFilter = { active: true, inactive: true };
  initialFormValues: any;
  isFormChanged: boolean = false;
  sortByAddedOn: boolean = true;
  showPassword: boolean = false;
  showEyeIcon: boolean = true;
  showCopyIcon: boolean = true;
  showGenerateText: boolean = true;

  showResetGenerateText = true;
  showResetEyeIcon = true;
  showResetCopyIcon = true;
  showResetPassword = false;
  sortByName: boolean = false;
  sortByNameAscending: boolean = false;
  sortByAddedOnAscending: boolean = true;

  constructor(
    public _data: DataService,
    private adminService: AdminService,
    private fb: FormBuilder,
    private messagingBus: MessagingService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.preventAdminSelfStatusModification();
    this.unmaskPage();
    this.loadUsers();
    this.initializeForm();
    this.initializeEditForm();
    this.initializeResetForm();
    this.subscription = this.messagingBus.subscribe(
      BUS_MESSAGE_KEY.USER_DETAILS,
      (data) => {
        this.currentLoggedInUser = data.userName;
      }
    );
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');

    const amOrPm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;

    return `${day}/${month}/${year}, ${hours}:${minutes} ${amOrPm}`;
  }

  unmaskPage(): void {
    this._data.apiCallInProgress = false;
    const overlay = document.getElementById('overlay');
    if (overlay) overlay.style.display = 'none';
  }

  maskPage(): void {
    this._data.apiCallInProgress = true;
    const overlay = document.getElementById('overlay');
    if (overlay) overlay.style.display = 'block';
  }

  loadUsers(): void {
    this.maskPage();
    this.adminService.getUsers().subscribe(
      (response: any[]) => {
        // Map response to ensure correct property names
        this.userarray = response.map((user) => ({
          ...user,
          isUserActive: Boolean(user.isUserActive),
          addedOn: user.added_on || '1970-01-01T00:00:00.000Z', // Rename 'added_on' to 'addedOn'
        }));

    

        // Sorting logic to ensure admins are on top and AddedOn is sorted by default
        if (this.sortByName) {
          // Sorting by Name
          this.userarray.sort((a, b) => {
            // Ensure admins are always on top, then sort by name
            if (a.isAdmin !== b.isAdmin) {
              return a.isAdmin ? -1 : 1; // Admins come first
            }
            return this.sortByNameAscending
              ? a.name.localeCompare(b.name) // Ascending by Name
              : b.name.localeCompare(a.name); // Descending by Name
          });
        } else if (this.sortByAddedOn) {
          // Sorting by AddedOn
          this.userarray.sort((a, b) => {
            // Ensure admins are always on top, then sort by AddedOn
            if (a.isAdmin !== b.isAdmin) {
              return a.isAdmin ? -1 : 1; // Admins come first
            }
            const dateA = new Date(a.addedOn).getTime();
            const dateB = new Date(b.addedOn).getTime();
            return this.sortByAddedOnAscending ? dateA - dateB : dateB - dateA; // Ascending or Descending by AddedOn
          });
        }

       

        this.totalUsers = this.userarray.length;
        this.updateDisplayedUsers();
        this.unmaskPage();
      },
      (error) => {
        this.handleError(error);
      }
    );
  }
  updateDisplayedUsers(): void {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = this.getEndingIndex();
    this.displayedUsers = this.userarray.slice(startIndex, endIndex);
    this.totalPages = this.getTotalPages();
  }

  getEndingIndex(): number {
    const endIndex = this.currentPage * this.itemsPerPage;
    return endIndex > this.totalUsers ? this.totalUsers : endIndex;
  }

  getTotalPages(): number {
    return Math.ceil(this.totalUsers / this.itemsPerPage);
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.getTotalPages()) {
      this.currentPage = page;
      this.updateDisplayedUsers();
    }
  }

  onItemsPerPageChange(): void {
    this.currentPage = 1;
    this.updateDisplayedUsers();
  }

  onAddUser(): void {
    this.addUser = true;
    this.FormLabel = 'Add User';
    console.log('Adding new user:', this.addUser);
  }

  closeModal(): void {
    this.addUser = false;
    this.editUser = false;
    this.resetPassword = false;
    this.showGenerateText = true;
    this.showCopyIcon = true;
    this.showEyeIcon = true;
    this.showResetGenerateText = true;
    this.showResetCopyIcon = true;
    this.showResetEyeIcon = true;

    this.addUserForm.reset(); // Reset the add user form
    this.editUserForm.reset();
    this.resetPasswordForm.reset();
  }

  initializeForm(): void {
    this.addUserForm = this.fb.group({
      name: ['', Validators.required],
      username: ['', Validators.required],
      password: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(15),
          Validators.pattern(
            /(?=.*[!@#$%^&*(),.?":{}|<>])(?=.*\d)(?=.*[a-z])(?=.*[A-Z])/
          ),
        ],
      ],
      accountStatus: ['', Validators.required], // 'accountStatus' will hold 'active' or 'inactive'
      projectRole: ['', Validators.required],
    });
  }

  onSubmitAddUser(): void {
    if (this.addUserForm.valid) {
      const newUserFormData = this.addUserForm.value;

      // Set the isUserActive value based on the accountStatus selection
      const isActive = newUserFormData.accountStatus === 'active' ? 1 : 0;

      // Set the isAdmin value based on the projectRole selection
      const isAdmin = newUserFormData.projectRole === 'admin' ? 1 : 0;

      const newUserPayload = {
        username: newUserFormData.username,
        name: newUserFormData.name,
        added_by: this.currentLoggedInUser,
        password: newUserFormData.password,
        isAdmin: isAdmin, // 1 for admin, 0 for user
        isUserActive: isActive, // 1 for active, 0 for inactive
      };

      this.addUser = false;

      this.maskPage(); // Show loading indicator
      this.adminService.addUserAdmin(newUserPayload).subscribe(
        (response) => {
          this.loadUsers(); // Reload the users list
          this.addUserForm.reset();
          this.closeModal(); // Close modal after submission
          this.unmaskPage(); // Hide loading indicator
          this.messageService.showMessage(response, 'success');
        },
        (error) => {
          this.handleError(error);
        }
      );
    } else {
      console.log('Form is invalid, cannot submit!');
    }
  }

  isFieldInvalid(field: string): boolean {
    const control = this.addUserForm.get(field);
    return control?.invalid && (control?.touched || control?.dirty);
  }

  preventAdminSelfStatusModification() {
    const token = localStorage.getItem('authToken');
    const tokenPayload: IBasicAuthUserBackendTokenPayload = jwtDecode(token);
    this.loggedInAdminUsername = tokenPayload.isAdmin
      ? tokenPayload.username
      : '';

    console.log('username of Admin', this.loggedInAdminUsername);
  }

  resetButtonEnablement(i) {
    const user = this.displayedUsers[i];
    return user.username === this.loggedInAdminUsername;
  }

  onEditUser(i: number): void {
    this.editUser = true;
    const user = this.displayedUsers[i];
    this.selectedUserToEdit = user;
    console.log('user', user);
    this.initialUserData = { ...user };
    this.editUserForm.patchValue({
      username: user.username,
      name: user.name,
      accountStatus: user.isUserActive === true ? 'Active' : 'InActive',
      projectRole: user.isAdmin ? 'Admin' : 'User',
    });
    this.editUserForm.get('username').disable();
    const formRawInputData = this.editUserForm.getRawValue();
    console.log('loggedInAdminUsername', this.loggedInAdminUsername);
    console.log('formRawInputData.username ', formRawInputData.username);
    if (formRawInputData.username === this.loggedInAdminUsername) {
      this.editUserForm.get('accountStatus').disable();
      this.editUserForm.get('projectRole').disable();
    } else {
      this.editUserForm.get('accountStatus').enable();
      this.editUserForm.get('projectRole').enable();
    }
    // storign intial form values
    this.initialFormValues = this.editUserForm.getRawValue();
    this.editUserForm.valueChanges.subscribe(() => {
      this.checkIfFormChanged();
    });
  }
  checkIfFormChanged(): void {
    const currentFormValues = this.editUserForm.getRawValue();
    this.isFormChanged = !this.areObjectsEqual(
      this.initialFormValues,
      currentFormValues
    );
  }
  areObjectsEqual(obj1: any, obj2: any): boolean {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
  }

  onSubmitEndUser() {
    if (this.editUserForm.valid) {
      const updatedUserFormData = this.editUserForm.getRawValue();
      const updatedUserPayload = {
        username: updatedUserFormData.username,
        name: updatedUserFormData.name,
        isUserActive: updatedUserFormData.accountStatus === 'Active' ? 1 : 0,
        isAdmin: updatedUserFormData.projectRole === 'Admin' ? 1 : 0,
      };

      this.selectedUserToEdit.isUserActive;

      if (
        Object.keys(updatedUserPayload).some(
          (key) => updatedUserPayload[key] != this.selectedUserToEdit[key]
        )
      ) {
        this.editUser = false;
        this.maskPage(); // Show loading indicator
        this.adminService.updateUserById(updatedUserPayload).subscribe(
          (response) => {
            console.log('User updated successfully:', response);
            this.loadUsers(); // Reload the users list
            this.closeModal(); // Close modal after submission
            this.editUserForm.reset();
            this.unmaskPage(); // Hide loading indicator
          },
          (error) => {
            this.handleError(error);
          }
        );
      }
    } else {
      console.log('Form is invalid, cannot submit!');
    }
  }

  initializeEditForm(): void {
    this.editUserForm = this.fb.group(
      {
        name: ['', Validators.required],
        username: [{ value: '' }],
        accountStatus: [''],
        projectRole: [''],
      },
      { validator: this.atLeastOneFieldFilledValidator }
    );
  }

  onResetUser(i: number): void {
    this.resetPasswordForm.get('username').disable();
    this.resetPassword = true;
    const user = this.displayedUsers[i].username;
    this.resetPasswordForm.patchValue({
      username: user,
    });
  }

  resetOnSubmit(): void {
    if (this.resetPasswordForm.valid) {
      const data = this.resetPasswordForm.getRawValue();
      const requestData = {
        username: data.username,
        newPassword: data.newPassword,
      };
      this.resetPassword = false;

      this.maskPage(); // Show loading indicator
      this.adminService.resetPasswordByUsername(requestData).subscribe(
        (response) => {
          this.closeModal(); // Close modal after submission
          this.resetPasswordForm.reset();
          this.unmaskPage(); // Hide loading indicator
        },
        (error) => {
          this.handleError(error);
        }
      );
    }
  }

  initializeResetForm(): void {
    this.resetPasswordForm = this.fb.group({
      username: [''],
      newPassword: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(15),
          Validators.pattern(
            /(?=.*[!@#$%^&*(),.?":{}|<>])(?=.*\d)(?=.*[a-z])(?=.*[A-Z])/
          ),
        ],
      ],
    });
  }

  handleError(error: any) {
    this.unmaskPage(); // Stop the spinner when error occurs

    // Check if there is a message from the backend
    const errorMessage =
      error.error?.message ||
      'An unexpected error occurred. Please try again later.';

    // Log the dynamic error message to the console and show it to the user
    console.error('Error:', errorMessage);
    this.messageService.showMessage(errorMessage, 'error');
  }

  toggleStatusFilterPopup(event: MouseEvent): void {
    this.showStatusFilterPopup = !this.showStatusFilterPopup;
    event.stopPropagation();
  }

  filterUsers(): void {
    this.displayedUsers = this.userarray.filter((user) => {
      if (this.statusFilter.active && user.isUserActive) {
        return true;
      }
      if (this.statusFilter.inactive && !user.isUserActive) {
        return true;
      }
      return false;
    });
  }
  isEditFormValid(): boolean {
    return (
      this.editUserForm.valid &&
      this.atLeastOneFieldFilledValidator(this.editUserForm) === null
    );
  }
  atLeastOneFieldFilledValidator(
    group: FormGroup
  ): { [key: string]: boolean } | null {
    const name = group.get('name')?.value;
    const accountStatus = group.get('accountStatus')?.value;
    const projectRole = group.get('projectRole')?.value;

    return name || accountStatus || projectRole
      ? null
      : { atLeastOneField: true };
  }

  generatePassword(isReset = false) {
    const length = Math.floor(Math.random() * (15 - 8 + 1)) + 8; // Random length between 8-15
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const specialChars = '!@#$%^&*-_=+';
    const allChars = uppercase + lowercase + numbers + specialChars;

    let password =
      uppercase[Math.floor(Math.random() * uppercase.length)] +
      lowercase[Math.floor(Math.random() * lowercase.length)] +
      numbers[Math.floor(Math.random() * numbers.length)] +
      specialChars[Math.floor(Math.random() * specialChars.length)];

    while (password.length < length) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }

    password = password
      .split('')
      .sort(() => Math.random() - 0.5)
      .join(''); // Shuffle password

    if (isReset) {
      this.resetPasswordForm.controls['newPassword'].setValue(password);
     
    } else {
      this.addUserForm.controls['password'].setValue(password);
     
    }
  }

  togglePasswordVisibility(isReset = false) {
    if (isReset) {
      this.showResetPassword = !this.showResetPassword;
      
    } else {
      this.showPassword = !this.showPassword;
     
    }
  }

  copyPassword(isReset = false) {
    const passwordInput = isReset
      ? (document.getElementById('resetPasswordInput') as HTMLInputElement)
      : (document.getElementById('passwordInput') as HTMLInputElement);

    if (passwordInput) {
      navigator.clipboard.writeText(passwordInput.value).then(() => {
        console.log('Password copied!');
      
      });
    }
  }
  toggleSortByAddedOn(): void {
    this.sortByAddedOn = true; // Ensure sorting by AddedOn is selected
    this.sortByAddedOnAscending = !this.sortByAddedOnAscending; // Toggle between ascending and descending
    this.sortByName = false; // Ensure Name sorting is not selected when toggling AddedOn sorting
    this.loadUsers(); // Re-load users after sorting
  }

  toggleSortByName(): void {
    this.sortByName = true; // Ensure sorting by Name is selected
    this.sortByNameAscending = !this.sortByNameAscending; // Toggle between ascending and descending
    this.sortByAddedOn = false; // Ensure AddedOn sorting is not selected when toggling Name sorting
    this.loadUsers(); // Re-load users after sorting
  }
}
